import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GetLoadsResponse } from "@/api/vendor/getLoads";
import { useLoadMutations } from "./mutations";
import { sonner } from "@/components/ui/useSonner";
import { useQuery } from "@tanstack/react-query";
import { getStations } from "@/api/receiver/getStations";
import { DateAndTimePicker } from "./DateAndTimePicker";
import { Separator } from "@/components/ui/separator";

interface UpdateLoadModalProps {
  load: GetLoadsResponse[0] | null;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function UpdateLoadModal({
  load,
  isOpen,
  setIsOpen,
}: UpdateLoadModalProps) {
  const toast = sonner();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    sourceStationId: "",
    destinationStationId: "",
    weight: "",
    pieces: "",
    notes: "",
    pickupStart: null as Date | null,
    pickupEnd: null as Date | null,
    deliveryStart: null as Date | null,
    deliveryEnd: null as Date | null,
  });

  // Get stations data
  const { data: stations } = useQuery({
    queryKey: ["stations", "receiver"],
    queryFn: () => getStations(),
  });

  // Pre-fill form when load changes
  useEffect(() => {
    if (load && stations) {
      // Find station IDs based on location names
      const sourceStation = stations.vendorStations?.find(
        (station) => station.name === load.sourceLocation
      );
      const destinationStation = stations.receiverStations?.find(
        (station) => station.name === load.destinationLocation
      );

      setFormData({
        sourceStationId: sourceStation?.id || "",
        destinationStationId: destinationStation?.id || "",
        weight: load.weight?.toString() || "",
        pieces: load.pieces?.toString() || "",
        notes: load.notes || "",
        pickupStart: load.pickupStart,
        pickupEnd: load.pickupEnd,
        deliveryStart: load.deliveryStart,
        deliveryEnd: load.deliveryEnd,
      });
    }
  }, [load, stations]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateTimeChange = (field: string, date: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));
  };

  const { updateLoad } = useLoadMutations();

  const validateForm = () => {
    const errors: string[] = [];

    if (!formData.sourceStationId) {
      errors.push("Source location is required");
    }
    if (!formData.destinationStationId) {
      errors.push("Destination location is required");
    }
    if (!formData.weight || isNaN(Number(formData.weight))) {
      errors.push("Valid weight is required");
    }
    if (!formData.pieces || isNaN(Number(formData.pieces))) {
      errors.push("Valid number of pieces is required");
    }
    if (!formData.pickupStart) {
      errors.push("Pickup start date and time are required");
    }
    if (!formData.pickupEnd) {
      errors.push("Pickup end date and time are required");
    }

    return errors;
  };

  const handleSave = async () => {
    if (!load) return;

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      toast.error("Validation Failed", {
        description: validationErrors.join(", "),
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await updateLoad.mutateAsync({
        loadId: load.id,
        sourceStation: formData.sourceStationId,
        destinationStation: formData.destinationStationId,
        weight: Number(formData.weight),
        pieces: Number(formData.pieces),
        notes: formData.notes,
        pickupStart: formData.pickupStart!,
        pickupEnd: formData.pickupEnd!,
        deliveryStart: formData.deliveryStart,
        deliveryEnd: formData.deliveryEnd,
      });

      toast.success("Load Updated", {
        description: "Load has been successfully updated",
      });
    } catch (error) {
      console.error("Error updating load:", error);
      toast.error("Failed to Update Load", {
        description: "Please try again or contact support",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!load) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Load {load.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Route Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Route Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sourceStationId">Source Location</Label>
                  <Select
                    value={formData.sourceStationId}
                    onValueChange={(value) =>
                      handleSelectChange("sourceStationId", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select pickup location" />
                    </SelectTrigger>
                    <SelectContent>
                      {stations?.vendorStations?.map((station) => (
                        <SelectItem key={station.id} value={station.id}>
                          {station.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="destinationStationId">
                    Destination Location
                  </Label>
                  <Select
                    value={formData.destinationStationId}
                    onValueChange={(value) =>
                      handleSelectChange("destinationStationId", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select destination location" />
                    </SelectTrigger>
                    <SelectContent>
                      {stations?.receiverStations?.map((station) => (
                        <SelectItem key={station.id} value={station.id}>
                          {station.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Load Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Load Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="weight">Weight (lbs)</Label>
                  <Input
                    id="weight"
                    name="weight"
                    type="number"
                    value={formData.weight}
                    onChange={handleInputChange}
                    placeholder="Enter weight"
                  />
                </div>
                <div>
                  <Label htmlFor="pieces">Number of Pieces</Label>
                  <Input
                    id="pieces"
                    name="pieces"
                    type="number"
                    value={formData.pieces}
                    onChange={handleInputChange}
                    placeholder="Enter number of pieces"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Enter any additional notes"
                  rows={3}
                />
              </div>
            </div>

            {/* Pickup Schedule */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Pickup Schedule</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <DateAndTimePicker
                    label="Pickup Start"
                    startDateTime={formData.pickupStart}
                    endDateTime={formData.pickupEnd}
                    onChange={(dateStart, dateEnd) => {
                      console.log("Pickup Start:", dateStart);
                      console.log("Pickup End:", dateEnd);

                      handleDateTimeChange("pickupStart", dateStart);
                      handleDateTimeChange("pickupEnd", dateEnd);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Delivery Schedule */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Delivery Schedule</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <DateAndTimePicker
                    label="Delivery Start"
                    startDateTime={formData.deliveryStart}
                    endDateTime={formData.deliveryEnd}
                    minDate={formData.pickupEnd} // Delivery cannot start before pickup ends
                    onChange={(dateStart, dateEnd) => {
                      handleDateTimeChange("deliveryStart", dateStart);
                      handleDateTimeChange("deliveryEnd", dateEnd);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <Separator className="my-2" />

          <DialogFooter className="flex justify-between">
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
